import { observable, flow, makeObservable, action, runInAction } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { BorrowerDetailsVerificationService, TrackRecordTrace, TrackRecordAgentResponse } from '@roc/feature-credit-background-check';
import { TrackRecordPropertyFormStore } from './trackRecordPropertyFormStore';
import { TrackRecordEntityFormStore } from './trackRecordEntityFormStore';
import { TrackRecordOtherFormStore } from './trackRecordOtherFormStore';
import { TRACK_RECORD_BORROWER_INPUTTED, TRACK_RECORD_USER_INPUTTED, UNDERWRITING, formatDate, isNil, isNotBlank, parseMMDDYYYYDate } from '@roc/feature-utils';
import { FileUpload } from '@roc/ui';
import { BorrowersService } from '../../borrowers/services/borrowersService';

const TRACK_RECORD_TASK_NAME = 'Track Record';
const BORROWER_TYPE_ID = 'BORROWER_ID';

const LICENSE_DOC_REVIEW_TASK_CODE = {
  BORROWER_GC_LICENSE: 'GC License',
  BORROWER_BROKER_LICENSE: 'Broker License',
};

export type PendingAgentSearch = {
  id: string;
  label: string;
  flow: 'ENTITY_LOOKUP' | 'PROPERTY_LOOKUP' | 'UPLOAD';
  queuedAt: number;
  confirmedAt: number | null;
  requestId: string | null;
  bedrockAgentInvocationId: number | null;
  status: 'pending' | 'done';
  finishedAt: number | null;
  resultCount: number | null;
};

export class BorrowerTrackRecordVerificationStore {
  globalStore: GlobalStore;
  private borrowerDetailsVerificationService: BorrowerDetailsVerificationService;
  private borrowersService: BorrowersService;
  trackRecordPropertyFormStore: TrackRecordPropertyFormStore;
  trackRecordEntityFormStore: TrackRecordEntityFormStore;
  trackRecordOtherFormStore: TrackRecordOtherFormStore;
  activeStep = 0;
  loading = false;
  properties: any[];
  trackRecordOverviewItems: any[];
  trackRecordOverview: any | null;
  isPropertyFormActive: boolean;
  currentTasks: any[];
  currentExpandedRowIndex: number;
  currentPropertyOnEdit: any;
  currentEntityOnEdit: any;
  currentDocumentsToUpload: any[];
  currentProperty: any;
  currentPropertyIndex: number;
  entities: any[];
  isEntityFormActive: boolean;
  currentEntity: any;
  currentEntityIndex: number;
  borrowerInfo: any;
  borrowerId: any;
  pdfTitle: string;
  pdfData: any;
  isPdfPreviewModalOpen: boolean;
  generalContractorUploads: any[];
  brokerUploads: any[];
  currentToken: string;
  borrowerTier;
  digitalTrackRecord: boolean;
  templateUploads: FileUpload[];
  trackRecordTemplateParsingAllowed: boolean;
  trackRecordsErrors: any;
  isTpoFlow: boolean;
  shouldPropagateDealType: boolean;
  extractionFailed: boolean;
  extractionComplete: boolean;
  extractionTraceSteps: TrackRecordTrace[];
  extractionResults: TrackRecordAgentResponse[];
  gcLicenseFileId: string | null;
  reLicenseFileId: string | null;
  gcLicenseTaskId: number | null;
  brokerLicenseTaskId: number | null;
  isUploadingLicense: boolean;
  alreadySavedRecords: any[];
  uploadedTrackRecordFile: { taskId: any; originalFileName: string; fileExtension: string } | null;
  pendingAgentSearches: PendingAgentSearch[];
  private pollHandle: any = null;
  private pendingSearchPollHandle: any = null;
  private pendingSearchCounter = 0;
  private isReconcilingPendingSearches = false;

  constructor(globalStore, borrowerDetailsVerificationService: BorrowerDetailsVerificationService) {
    this.globalStore = globalStore;
    this.borrowerDetailsVerificationService = borrowerDetailsVerificationService;
    this.trackRecordPropertyFormStore = new TrackRecordPropertyFormStore(globalStore);
    this.trackRecordEntityFormStore = new TrackRecordEntityFormStore(globalStore);
    this.trackRecordOtherFormStore = new TrackRecordOtherFormStore(globalStore);
    this.borrowersService = new BorrowersService();
    this.properties = [];
    this.trackRecordOverviewItems = [];
    this.trackRecordOverview = null;
    this.currentTasks = [];
    this.currentExpandedRowIndex = null;
    this.currentProperty = {};
    this.currentPropertyIndex = null;
    this.entities = [];
    this.borrowerInfo = {};
    this.borrowerId = null;
    this.pdfTitle = '';
    this.pdfData = null;
    this.isPdfPreviewModalOpen = false;
    this.generalContractorUploads = [];
    this.brokerUploads = [];
    this.currentToken = null;
    this.currentDocumentsToUpload = [];
    this.digitalTrackRecord = true;
    this.templateUploads = [];
    this.trackRecordTemplateParsingAllowed = false;
    this.trackRecordsErrors = {};
    this.shouldPropagateDealType = true;
    this.extractionFailed = false;
    this.extractionComplete = false;
    this.extractionTraceSteps = [];
    this.extractionResults = [];
    this.gcLicenseFileId = null;
    this.reLicenseFileId = null;
    this.gcLicenseTaskId = null;
    this.brokerLicenseTaskId = null;
    this.isUploadingLicense = false;
    this.alreadySavedRecords = [];
    this.uploadedTrackRecordFile = null;
    this.pendingAgentSearches = [];
    this.pollHandle = null;
    this.pendingSearchPollHandle = null;

    makeObservable(this, {
      activeStep: observable,
      loading: observable,
      properties: observable,
      trackRecordOverviewItems: observable,
      trackRecordOverview: observable,
      isPropertyFormActive: observable,
      currentTasks: observable,
      currentExpandedRowIndex: observable,
      currentPropertyOnEdit: observable,
      currentEntityOnEdit: observable,
      currentDocumentsToUpload: observable,
      currentProperty: observable,
      currentPropertyIndex: observable,
      entities: observable,
      currentEntity: observable,
      currentEntityIndex: observable,
      isEntityFormActive: observable,
      fetchBorrowerInfo: flow,
      fetchTrackRecordProperties: flow,
      fetchTrackRecordOverview: flow,
      loadFromOverview: flow,
      applyOverview: action,
      fetchBorrowerInfoAndEntities: flow,
      getLoanBorrowerDocuments: flow,
      resolveLicensesByLoanAndBorrower: flow,
      resolveLicensesByBorrower: flow,
      setTrackRecordFileByTaskId: flow,
      fetchLicensesByTaskId: flow,
      fetchLicenseFileByTaskId: flow,
      fetchUploadedTrackRecordFile: flow,
      getBorrowerBaseballCardData: flow,
      getVerifiedTrackRecordProperties: flow,
      getUnverifiedTrackRecordProperties: flow,
      resetStore: flow,
      goNextStep: flow,
      goPrevStep: flow,
      setCurrentExpandedRowIndex: flow,
      saveTrackRecordProperty: flow,
      addTrackRecordProperty: flow,
      editTrackRecordProperty: flow,
      handleAddNewTrackRecordProperty: action,
      setPropertyFormActive: action,
      handleEditExistingTrackRecordProperty: action,
      removeTrackRecordProperty: flow,
      handleRemoveTrackRecordProperty: flow,
      saveEntityFromProperty: flow,
      getTrackRecordPropertyJson: action,
      saveTrackRecordEntity: flow,
      addTrackRecordEntity: flow,
      editTrackRecordEntity: flow,
      removeTrackRecordEntity: flow,
      handleAddNewTrackRecordEntity: action,
      handleEditExistingTrackRecordEntity: action,
      handleRemoveTrackRecordEntity: flow,
      getTrackRecordEntityJson: action,
      getTasks: flow,
      getTasksWithBody: flow,
      createTaskWithFile: flow,
      createBorrowerLicenseTask: flow,
      uploadTaskFile: flow,
      downloadTaskFile: flow,
      pdfTitle: observable,
      pdfData: observable,
      isPdfPreviewModalOpen: observable,
      saveAndUploadGCAndBrokerData: flow,
      saveEligibilityAnswer: flow,
      getGCAndBrokerBody: action,
      saveAndUploadTask: flow,
      generalContractorUploads: observable,
      brokerUploads: observable,
      setGeneralContractorUploads: flow,
      setBrokerUploads: flow,
      currentToken: observable,
      setCurrentToken: flow,
      borrowerTier: observable,
      setBorrowerInfo: flow,
      resetActiveStep: action,
      digitalTrackRecord: observable,
      setDigitalTrackRecord: flow,
      templateUploads: observable,
      setTemplateUploads: flow,
      validateTrackRecordTemplate: flow,
      parseTrackRecordTemplate: flow,
      isTrackRecordTemplateParsingAllowed: flow,
      trackRecordsErrors: observable,
      doesPropertyHasParsingError: action,
      areValidParsedTrackRecordProperties: action,
      continueFromTrackRecordStep: flow,
      setIsTpoFlow: action,
      isTpoFlow: observable,
      checkBorrowerId: action,
      shouldPropagateDealType: observable,
      setShouldPropagateDealType: action,
      extractionFailed: observable,
      extractionComplete: observable,
      extractionTraceSteps: observable,
      extractionResults: observable,
      gcLicenseFileId: observable,
      reLicenseFileId: observable,
      gcLicenseTaskId: observable,
      brokerLicenseTaskId: observable,
      alreadySavedRecords: observable,
      uploadedTrackRecordFile: observable,
      pendingAgentSearches: observable,
      queueTrackRecordSearch: action,
      dismissAgentSearch: action,
      reconcilePendingAgentSearches: flow,
      setAlreadySavedRecords: action,
      parseUploadedTrackRecord: flow,
      resetExtractionState: action,
      invokeAgent: action,
      invokeAgentFireAndForget: action,
      uploadLicenseFile: flow,
      markLicenseClaimed: action,
      isUploadingLicense: observable,
      setUploadingLicense: flow,
      cancelExtraction: action,
      saveExtractionResults: flow,
      uploadGCAndBrokerLicenses: flow,
    });
    this.setDefaults();
  }

  private setDefaults() {
    this.activeStep = 0;
    this.loading = false;
    this.isPropertyFormActive = false;
    this.currentTasks = [];
    this.currentExpandedRowIndex = null;
    this.currentProperty = {};
    this.currentPropertyIndex = null;
    this.isEntityFormActive = false;
    this.currentEntity = {};
    this.currentEntityIndex = null;
    this.digitalTrackRecord = true;
  }

  *resetStore() {
    this.trackRecordPropertyFormStore.reset();
    this.trackRecordEntityFormStore.reset();
    this.trackRecordOtherFormStore.reset();
    this.setDefaults();
  }

  *goPrevStep() {
    this.activeStep--;
    this.currentExpandedRowIndex = null;
  }

  *goNextStep() {
    this.activeStep++;
    this.currentExpandedRowIndex = null;
  }

  *setCurrentExpandedRowIndex(value: number) {
    this.currentExpandedRowIndex = value;
  }

  *fetchBorrowerInfo() {
    try {
      if (isNil(this.borrowerInfo.borrowerId)) {
        const response = yield this.borrowerDetailsVerificationService.getBorrowerInfoByToken(this.currentToken);
        this.borrowerInfo = response.data.data;
      }
      yield this.fetchBorrowerInfoAndEntities();
      yield this.fetchTrackRecordProperties();
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while retrieving borrower info.',
      });
    }
  }

  *fetchBorrowerInfoAndEntities() {
    const borrowerId = this.borrowerInfo?.borrowerId || this.borrowerId;
    const baseballCard = yield this.getBorrowerBaseballCardData(borrowerId);
    this.entities = [...(baseballCard?.data?.borrowingEntities || [])];
    this.borrowerInfo = {
      ...baseballCard?.data,
      borrowerId: baseballCard?.data?.borrowerId ?? baseballCard?.data?.id ?? borrowerId,
    };
    this.setBorrowerFields();
  }

  setBorrowerFields() {
    const hasBrokerLicense = !!this.borrowerInfo?.isBroker || (this.brokerUploads?.length > 0);
    const hasGcLicense = !!this.borrowerInfo?.isGeneralContractor || (this.generalContractorUploads?.length > 0);
    this.trackRecordOtherFormStore.onFieldChange('broker', hasBrokerLicense ? 'Yes' : 'No');
    this.trackRecordOtherFormStore.onFieldChange('generalContractor', hasGcLicense ? 'Yes' : 'No');
    // Only sync previousExperience when the server has an explicit value. If the
    // borrower hasn't saved yet (e.g. selected Yes then uploaded a template), keep
    // the current form selection instead of resetting to No.
    if (!isNil(this.borrowerInfo?.previousExperience)) {
      this.trackRecordOtherFormStore.onFieldChange(
        'previousExperience',
        this.borrowerInfo.previousExperience ? 'Yes' : 'No'
      );
    }
  }

  *fetchTrackRecordProperties() {
    const verifiedTrackRecord = yield this.getVerifiedTrackRecordProperties(this.borrowerInfo.borrowerId);
    const unverifiedTrackRecord = yield this.getUnverifiedTrackRecordProperties(this.borrowerInfo.borrowerId);
    this.properties = [...verifiedTrackRecord?.data, ...unverifiedTrackRecord?.data];
    const borrowerTierResponse = yield this.borrowerDetailsVerificationService.getBorrowerUnverifiedTierRating(this.currentToken, this.borrowerInfo?.borrowerId);
    this.borrowerTier = borrowerTierResponse?.data?.data;
  }

  // Checked on demand (the "Refresh" button), not polled. Returns the parsed
  // items so the caller can show a "still processing" state.
  *fetchTrackRecordOverview(borrowerId: number | string) {
    const response = yield this.borrowerDetailsVerificationService.getTrackRecordOverview(borrowerId);
    // The endpoint's body is the overview object itself (borrower_id, summary,
    // properties, entities, *_agent_invocation lists, files, ...) — not wrapped in a `data` envelope.
    const overview = response?.data ?? null;
    this.trackRecordOverview = overview;
    // All three lists, so a run that already finished still matches its pending search by request_id.
    const items = [
      ...(overview?.in_progress_agent_invocation ?? []),
      ...(overview?.error_agent_invocation ?? []),
      ...(overview?.success_agent_invocation ?? []),
    ];
    this.trackRecordOverviewItems = items;
    return items;
  }

  private mapOverviewProperty(overviewProperty: any, borrowerId: number | string) {
    return {
      verifiedTrackRecordsId: overviewProperty.track_records_v2_id,
      // verifyTrackRecord posts this object back; without borrowerId the portal re-keys the row under null.
      borrowerId,
      purchaseAddress: overviewProperty.purchase_address,
      purchaseSecondaryAddress: overviewProperty.purchase_secondary_address,
      purchaseCity: overviewProperty.purchase_city,
      purchaseState: overviewProperty.purchase_state,
      purchaseZipcode: overviewProperty.purchase_zipcode,
      purchaseDate: overviewProperty.purchase_date,
      purchasePrice: overviewProperty.purchase_price,
      purchasedFrom: overviewProperty.purchased_from,
      saleDate: overviewProperty.sale_date,
      salePrice: overviewProperty.sale_price,
      soldTo: overviewProperty.sold_to,
      rehabBudget: overviewProperty.rehab_budget,
      dealType: overviewProperty.deal_type,
      propertyType: overviewProperty.property_type,
      entityName: overviewProperty.entity_name,
      bridge: overviewProperty.bridge,
      groundUp: overviewProperty.ground_up,
      term: overviewProperty.term,
      isVerified: overviewProperty.is_verified,
      verificationInProgress: overviewProperty.verification_in_progress,
      // undefined when the payload omits it, so applyOverview can keep the optimistic local flag.
      isVerificationRequested: overviewProperty.is_verification_requested ?? overviewProperty.isVerificationRequested,
      recordSourceType: overviewProperty.record_source_type,
      verificationSource: overviewProperty.verification_source,
      mortgageAmount: overviewProperty.mortgage_amount,
      lenderName: overviewProperty.lender_name,
      comment: overviewProperty.comment,
      links: overviewProperty.links,
      lastUpdateDate: overviewProperty.last_update_date,
      lastUpdatedBy: overviewProperty.last_updated_by,
      bedrockAgentInvocationId: overviewProperty.bedrock_agent_invocation_id,
    };
  }

  private mapOverviewEntity(overviewEntity: any) {
    return {
      borrowerEntityBorrowerId: overviewEntity.borrower_entity_borrower_id,
      borrowerEntityId: overviewEntity.borrower_entity_id,
      entityName: overviewEntity.entity_name,
      entityState: overviewEntity.entity_state,
      entityVerified: overviewEntity.verified ? 'Yes' : 'No',
      verificationSource: overviewEntity.verification_source,
      isEntityPresentInLoan: overviewEntity.present_in_loan ? 'true' : 'false',
    };
  }

  private toOverviewLicenseUpload(file: any) {
    const doc = file?.documents?.[0];
    if (!doc) return null;
    return this.toLicenseFileUpload({
      taskId: file.task_id,
      originalFileName: doc.original_file_name,
      fileExtension: (doc.original_file_name || '').split('.').pop(),
    });
  }

  // v2 single-request load: populates properties/entities/licenses/borrowerInfo
  // from the overview payload.
  *loadFromOverview(borrowerId?: number | string) {
    this.checkBorrowerId(borrowerId);
    const id = borrowerId ?? this.borrowerInfo?.borrowerId ?? this.borrowerId;
    if (!id) return [];

    const items = yield this.fetchTrackRecordOverview(id);
    this.applyOverview(id);
    return items;
  }

  // Maps the already-fetched this.trackRecordOverview onto state (no GET) — reused by reconcile.
  applyOverview(id: number | string) {
    const overview = this.trackRecordOverview;
    if (!overview) return;

    const summary = overview.summary || {};
    const experienceQuestions = overview.experience_questions || {};
    const files = overview.files || {};

    this.borrowerInfo = {
      ...this.borrowerInfo,
      borrowerId: overview.borrower_id ?? id,
      firstName: overview.first_name,
      lastName: overview.last_name,
      isGeneralContractor: !!experienceQuestions.is_general_contractor,
      isBroker: !!experienceQuestions.is_broker,
      previousExperience: experienceQuestions.previous_experience,
      lockedData: summary.locked_data,
    };

    // Preserve the optimistic isVerificationRequested flag until the payload echoes it back.
    const previouslyRequestedIds = new Set(
      (this.properties ?? [])
        .filter((property: any) => property.isVerificationRequested)
        .map((property: any) => property.verifiedTrackRecordsId)
        .filter((value: any) => value != null),
    );
    this.properties = [
      ...(overview.properties?.verified || []),
      ...(overview.properties?.unverified || []),
    ].filter(Boolean).map((rawProperty) => {
      const mapped = this.mapOverviewProperty(rawProperty, this.borrowerInfo.borrowerId);
      if (mapped.isVerificationRequested == null && previouslyRequestedIds.has(mapped.verifiedTrackRecordsId)) {
        mapped.isVerificationRequested = true;
      }
      return mapped;
    });

    this.entities = (overview.entities?.items || []).filter(Boolean).map((rawEntity) => this.mapOverviewEntity(rawEntity));

    const gcRow = this.toOverviewLicenseUpload(files.gc_license);
    const brokerRow = this.toOverviewLicenseUpload(files.broker_license);
    this.gcLicenseTaskId = files.gc_license?.task_id ?? null;
    this.brokerLicenseTaskId = files.broker_license?.task_id ?? null;
    this.generalContractorUploads = gcRow ? [gcRow] : [];
    this.brokerUploads = brokerRow ? [brokerRow] : [];

    const trackRecordDoc = files.track_record?.documents?.[0];
    this.uploadedTrackRecordFile = trackRecordDoc
      ? {
          taskId: files.track_record.task_id,
          originalFileName: trackRecordDoc.original_file_name,
          fileExtension: (trackRecordDoc.original_file_name || '').split('.').pop(),
        }
      : null;

    this.setBorrowerFields();
  }

  *getBorrowerBaseballCardData(borrowerId: number, loanId?: number) {
    const response = yield this.borrowersService.getBaseballCardDetails(
      borrowerId,
      loanId
    );
    return { data: JSON.parse(response?.data?.data || '[]') };
  }

  *getVerifiedTrackRecordProperties(borrowerId: number) {
    const response = yield this.borrowerDetailsVerificationService.getVerifiedTrackRecordProperties(borrowerId);
    return { data: JSON.parse(response?.data?.data || '[]') };
  }

  *getUnverifiedTrackRecordProperties(borrowerId: number) {
    const response = yield this.borrowerDetailsVerificationService.getUnverifiedTrackRecordProperties(borrowerId);
    return { data: JSON.parse(response?.data?.data || '[]') };
  }

  handleAddNewTrackRecordProperty() {
    this.currentDocumentsToUpload = [];
    this.currentPropertyOnEdit = {};
    this.isPropertyFormActive = true;
    this.currentProperty = {};
    this.currentPropertyIndex = this.properties.length + 1;
    this.trackRecordPropertyFormStore.reset();
  }

  setPropertyFormActive(value) {
    this.isPropertyFormActive = value;
  }

  handleEditExistingTrackRecordProperty(property, currentIndex) {
    this.currentDocumentsToUpload = this.currentTasks.filter(task => task.originalFileName)
      .map(task => {
        return {
          disabled: true,
          name: task.originalFileName,
          file: {
            path: task.originalFileName,
            name: task.originalFileName,
          }
        }
      });

    this.currentPropertyOnEdit = { ...property };
    this.currentProperty = { ...property };
    this.currentPropertyIndex = currentIndex + 1;
    this.trackRecordPropertyFormStore.loadForm(property);
    if (this.doesPropertyHasParsingError(property.purchaseAddress)) {
      this.trackRecordPropertyFormStore.runFormValidation();
    }
    this.isPropertyFormActive = true;
  }

  *saveTrackRecordProperty() {
    try {
      if (this.currentProperty.verifiedTrackRecordsId != null) {
        yield this.editTrackRecordProperty();
      }
      else {
        yield this.addTrackRecordProperty();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while saving track record property',
      });
    }
  }

  *addTrackRecordProperty() {
    try {
      this.loading = true;
      this.trackRecordPropertyFormStore.runFormValidation();
      if (!this.trackRecordPropertyFormStore.form.meta.isValid) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error occurred while saving track record property',
        });
        return;
      }
      if (this.trackRecordPropertyFormStore.form.meta.isValid) {
        const data = this.getTrackRecordPropertyJson();
        const response = yield this.borrowerDetailsVerificationService.saveTrackRecordProperty(data);

        if (!this.currentPropertyOnEdit) {
          this.currentPropertyOnEdit = {}
        }

        this.currentPropertyOnEdit.verifiedTrackRecordsId = JSON.parse(response.data.data).verifiedTrackRecordsId;

        this.currentDocumentsToUpload.forEach((document) => {
          if (!document.disabled) {
            const taskPropertyBody = {
              sectionProcess: 'TRACK_RECORD_REVIEW',
              objectType: 'PROPERTY_TRACK_RECORD',
              taskTemplateType: 'PROPERTY_TRACK_RECORD',
              objectId: this.currentPropertyOnEdit.verifiedTrackRecordsId,
            };
            this.createTaskWithFile(taskPropertyBody, document);
            document.disabled = true;
          }
        });
        yield this.saveEntityFromProperty(data);
        yield this.fetchTrackRecordProperties();
        yield this.setPropertyFormActive(false);
        this.trackRecordPropertyFormStore.reset();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while adding track record property',
      });
    } finally {
      this.loading = false;
    }
  }

  getTrackRecordPropertyJson() {
    const propertyFields = this.trackRecordPropertyFormStore.form.fields;
    if (this.shouldPropagateDealType) {
      this.setCheckBoxFields(propertyFields);
    }
    else {
      this.setDealTypeFromCheckBoxes(propertyFields);
    }
    if (!this.borrowerInfo) {
      this.borrowerInfo = {};
      this.borrowerInfo.borrowerId = this.borrowerId
    }
    else if (!this.borrowerInfo.borrowerId) {
      this.borrowerInfo.borrowerId = this.borrowerId
    }
    return {
      ...this.currentPropertyOnEdit,
      ...this.trackRecordPropertyFormStore.getFormValues(),
      purchaseDate: formatDate(propertyFields.purchaseDate.value, 'MM/dd/yyyy'),
      saleDate: formatDate(propertyFields.saleDate.value, 'MM/dd/yyyy'),
      borrowerId: this.borrowerInfo?.borrowerId ? this.borrowerInfo?.borrowerId : this.borrowerId,
      recordSourceType: this.isTpoFlow ? TRACK_RECORD_USER_INPUTTED : TRACK_RECORD_BORROWER_INPUTTED,
      purchaseSecondaryAddress: propertyFields?.address2.value,
    }
  }

  setCheckBoxFields(propertyFields) {
    switch (propertyFields?.dealType?.value) {
      case 'Bridge':
        propertyFields.bridge.value = true;
        propertyFields.term.value = true;
        propertyFields.groundUp.value = false;
        break;
      case 'Rental':
        propertyFields.term.value = true;
        propertyFields.bridge.value = false;
        propertyFields.groundUp.value = false;
        break;
    }
  }

  setDealTypeFromCheckBoxes(propertyFields) {
    if (propertyFields.groundUp.value) {
      propertyFields.dealType.value = 'Ground Up';
    }
    else if (propertyFields.bridge.value) {
      propertyFields.dealType.value = 'Bridge';
    }
    else if (propertyFields.term.value) {
      propertyFields.dealType.value = 'Rental';
    }
  }

  *editTrackRecordProperty() {
    try {
      this.loading = true;
      this.trackRecordPropertyFormStore.runFormValidation();
      if (!this.trackRecordPropertyFormStore.form.meta.isValid) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error occurred while saving track record property',
        });
      }
      if (this.trackRecordPropertyFormStore.form.meta.isValid) {
        const data = this.getTrackRecordPropertyJson();
        yield this.borrowerDetailsVerificationService.editTrackRecordProperty(data);
        this.currentDocumentsToUpload.forEach((document) => {
          if (!document.disabled) {
            const taskPropertyBody = {
              sectionProcess: 'TRACK_RECORD_REVIEW',
              objectType: 'PROPERTY_TRACK_RECORD',
              taskTemplateType: 'PROPERTY_TRACK_RECORD',
              objectId: this.currentPropertyOnEdit.verifiedTrackRecordsId,
            };
            this.createTaskWithFile(taskPropertyBody, document);
            document.disabled = true;
          }
        });
        yield this.fetchTrackRecordProperties();
        if (this.doesPropertyHasParsingError(data.purchaseAddress)) {
          this.trackRecordsErrors = {
            ...this.trackRecordsErrors,
            [data.purchaseAddress]: []
          }
        }
        yield this.setPropertyFormActive(false);
        this.trackRecordPropertyFormStore.reset();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while editing track record property',
      });
    } finally {
      this.loading = false;
    }
  }

  *handleRemoveTrackRecordProperty(property) {
    this.currentProperty = { ...property };
    yield this.removeTrackRecordProperty();
  }

  *removeTrackRecordProperty() {
    try {
      this.loading = true;
      if (this.currentProperty.verifiedTrackRecordsId != null) {
        yield this.borrowerDetailsVerificationService.deleteTrackRecordProperty(this.currentProperty.verifiedTrackRecordsId);
        yield this.fetchTrackRecordProperties();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while removing track record property',
      });
    } finally {
      this.loading = false;
    }
  }

  *saveEntityFromProperty(property: any) {
    try {
      const newEntity = {
        entityName: property.entityName,
        entityState: property.purchaseState
      };
      this.handleEditExistingTrackRecordEntity(newEntity, this.entities.length + 1);
      this.addTrackRecordEntity();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'We had an issue while loading entities.',
      });
    }
  }

  handleAddNewTrackRecordEntity() {
    this.currentDocumentsToUpload = [];
    this.currentEntityOnEdit = {};
    this.isEntityFormActive = true;
    this.currentEntity = {};
    this.currentEntityIndex = this.entities.length + 1;
    this.trackRecordEntityFormStore.reset();
  }

  handleEditExistingTrackRecordEntity(entity, currentIndex) {
    this.currentDocumentsToUpload = this.currentTasks.filter(task => task.originalFileName)
      .map(task => {
        return {
          disabled: true,
          name: task.originalFileName,
          file: {
            path: task.originalFileName,
            name: task.originalFileName,
          }
        }
      });

    this.currentEntityOnEdit = { ...entity };
    this.isEntityFormActive = true;
    this.currentEntity = { ...entity };
    this.currentEntityIndex = currentIndex + 1;
    this.trackRecordEntityFormStore.loadForm(entity);
  }

  *saveTrackRecordEntity() {
    try {
      if (this.currentEntity.borrowerEntityBorrowerId != null) {
        yield this.editTrackRecordEntity();
      }
      else {
        yield this.addTrackRecordEntity();
      }

      this.currentDocumentsToUpload.forEach((document) => {
        if (!document.disabled) {
          const taskEntityBody = {
            sectionProcess: 'TRACK_RECORD_REVIEW',
            objectType: 'BORROWER_ENTITY_BORROWER',
            taskTemplateType: 'BORROWER_ENTITY_BORROWER',
            objectId: this.currentEntityOnEdit.borrowerEntityBorrowerId,
          };
          this.createTaskWithFile(taskEntityBody, document);
          document.disabled = true;
        }
      });
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while saving track record entity',
      });
    }
  }

  *addTrackRecordEntity() {
    try {
      this.loading = true;
      this.trackRecordEntityFormStore.runFormValidation();
      if (this.trackRecordEntityFormStore.form.meta.isValid) {
        const data = this.getTrackRecordEntityJson();
        const response = yield this.borrowerDetailsVerificationService.saveTrackRecordEntity(data, this.borrowerInfo.borrowerId);
        this.currentEntityOnEdit.borrowerEntityBorrowerId = JSON.parse(response.data.data).saveBorrowerEntityBorrowerTrackRecord;
        yield this.fetchBorrowerInfoAndEntities();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while saving track record entity',
      });
    } finally {
      this.loading = false;
    }
  }

  getTrackRecordEntityJson() {
    const entityFields = this.trackRecordEntityFormStore.form.fields;
    return {
      ...this.currentEntity,
      ...this.trackRecordEntityFormStore.getFormValues(),
      name: entityFields.entityName.value,
      state: entityFields.entityState.value,
      borrowerId: this.borrowerInfo?.borrowerId,
    }
  }

  *editTrackRecordEntity() {
    try {
      this.loading = true;
      this.trackRecordEntityFormStore.runFormValidation();
      if (this.trackRecordEntityFormStore.form.meta.isValid) {
        const data = this.getTrackRecordEntityJson();
        yield this.borrowerDetailsVerificationService.editTrackRecordEntity(data, this.currentEntity.borrowerEntityId);
        yield this.fetchBorrowerInfoAndEntities();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while editing track record entity',
      });
    } finally {
      this.loading = false;
    }
  }

  *handleRemoveTrackRecordEntity(entity) {
    this.currentEntity = { ...entity };
    yield this.removeTrackRecordEntity();
  }

  *removeTrackRecordEntity() {
    try {
      this.loading = true;
      if (this.currentEntity.borrowerEntityBorrowerId != null) {
        yield this.borrowerDetailsVerificationService.removeTrackRecordEntity(this.currentEntity.borrowerEntityBorrowerId);
        yield this.fetchBorrowerInfoAndEntities();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while removing track record entity',
      });
    } finally {
      this.loading = false;
    }
  }

  *getTasksWithBody(id) {
    const taskPropertyBody = {
      sectionProcess: 'TRACK_RECORD_REVIEW',
      objectType: 'PROPERTY_TRACK_RECORD',
      taskTemplateType: 'PROPERTY_TRACK_RECORD',
      objectId: id,
    };
    const taskBody = taskPropertyBody;

    this.currentTasks = [];
    const response = yield this.borrowerDetailsVerificationService.getTasks(taskBody);
    this.currentTasks = response.data.data;
  }

  *getTasks(taskBody) {
    const response = yield this.borrowerDetailsVerificationService.getTasks(taskBody);
    this.currentTasks = response.data.data;
  }

  *createTaskWithFile(taskBody, file) {
    try {
      const taskResponse = yield this.borrowerDetailsVerificationService.createTask(taskBody);
      const task = taskResponse.data?.data[0];

      const uploadResponse = yield this.uploadTaskFile(task, file);
      const uploaded = uploadResponse?.data?.data;
      const uploadedTask = Array.isArray(uploaded) ? uploaded[0] : uploaded;

      yield this.getTasks(taskBody);
      // Prefer the post-upload fileDownloadId — the task created above
      // didn't have a file attached yet when createTask returned.
      return { ...task, fileDownloadId: uploadedTask?.fileDownloadId || task?.fileDownloadId };
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while uploading the file' + e.error.message,
      });
    }
  }

  // Create a license task and return it. Returns null on failure.
  *createBorrowerLicenseTask(taskBody) {
    try {
      const taskResponse = yield this.borrowerDetailsVerificationService.createTask(taskBody);
      return taskResponse.data?.data?.[0] ?? null;
    } catch (e) {
      console.error(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while creating the license task',
      });
      return null;
    }
  }

  *uploadTaskFile(task, fileUpload: FileUpload) {
    const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
    const newFile = new File([blob], fileUpload.name, { type: fileUpload.file.type });
    const formData = new FormData();
    formData.append('file', newFile);
    formData.append('task', JSON.stringify(task));
    return yield this.borrowerDetailsVerificationService.uploadTaskFile(formData);
  }

  *downloadTaskFile(id, name, fileExtension) {
    try {
      const blob = yield this.borrowerDetailsVerificationService.downloadTaskFile(id);
      const reader = new FileReader();

      reader.readAsArrayBuffer(blob);

      reader.onloadend = () => {
        if (fileExtension === 'pdf') {
          // Preview the PDF
          this.pdfTitle = name;
          this.pdfData = reader.result; // The ArrayBuffer
          this.isPdfPreviewModalOpen = true;
        } else {
          // Download the file if not a PDF
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = name;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      };

    } catch (error) {
      console.error(error);
    }
  }

  *saveAndUploadGCAndBrokerData(fetchBorrowerDetailsVerification) {
    try {
      this.loading = true;
      this.trackRecordOtherFormStore.runFormValidation();
      if (this.trackRecordOtherFormStore.form.meta.isValid) {
        const data = this.getGCAndBrokerBody();
        yield this.borrowerDetailsVerificationService.saveBorrowerGCAndBrokerData(data, this.currentToken);
        if (this.trackRecordOtherFormStore.form.fields.generalContractor.value === 'Yes') {
          const taskBody = {
            sectionProcess: 'BORROWER_GC_LICENSE',
            objectType: 'BORROWER',
            taskTemplateType: 'BORROWER_GC_LICENSE',
            objectId: this.borrowerInfo.borrowerId,
          };
          const gcTask = yield this.saveAndUploadTask(taskBody, this.generalContractorUploads);
          if (gcTask) {
            // Use the fileDownloadId directly as the agent reference. The old
            // getFilePath call has no real route (always 404) — dropped entirely.
            this.gcLicenseFileId = gcTask?.fileDownloadId || gcTask?.taskId?.toString() || null;
          }
        }
        if (this.trackRecordOtherFormStore.form.fields.broker.value === 'Yes') {
          const taskBody = {
            sectionProcess: 'BORROWER_BROKER_LICENSE',
            objectType: 'BORROWER',
            taskTemplateType: 'BORROWER_BROKER_LICENSE',
            objectId: this.borrowerInfo.borrowerId,
          };
          const brokerTask = yield this.saveAndUploadTask(taskBody, this.brokerUploads);
          if (brokerTask) {
            // Use the fileDownloadId directly as the agent reference. The old
            // getFilePath call has no real route (always 404) — dropped entirely.
            this.reLicenseFileId = brokerTask?.fileDownloadId || brokerTask?.taskId?.toString() || null;
          }
        }
        this.resetStore();
        fetchBorrowerDetailsVerification(this.currentToken);
        this.goNextStep();
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while saving borrower GC and Broker details',
      });
    } finally {
      this.loading = false;
    }
  }

  // Persists a single Yes/No eligibility answer right away; reverts the field if the save fails.
  *saveEligibilityAnswer(key: string, value: string) {
    const field = this.trackRecordOtherFormStore.form.fields[key];
    const previousValue = field?.value;
    this.trackRecordOtherFormStore.onFieldChange(key, value);
    try {
      yield this.borrowerDetailsVerificationService.saveBorrowerGCAndBrokerData(
        this.getGCAndBrokerBody(),
        this.currentToken
      );
    } catch (error) {
      console.error(error);
      this.trackRecordOtherFormStore.onFieldChange(key, previousValue);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while saving your answer',
      });
    }
  }

  *saveAndUploadTask(taskBody, files) {
    if (files.length > 0 && (files[0]?.file instanceof Blob || files[0]?.file instanceof File)) {
      return yield this.createTaskWithFile(taskBody, files[0]);
    }
  }

  /**
   * Intentionally a no-op: the upload and getFilePath logic moved into
   * saveAndUploadGCAndBrokerData. Kept as an awaited MobX flow so the
   * existing call sites (handleNext / handleCardClick) don't need to change;
   * it can be removed together with its call sites in a follow-up cleanup.
   */
  *uploadGCAndBrokerLicenses() {
    // No-op — see doc comment above.
  }

  getGCAndBrokerBody() {
    const borrowerFields = this.trackRecordOtherFormStore.form.fields;

    return {
      generalContractor: borrowerFields.generalContractor.value === 'Yes',
      broker: borrowerFields.broker.value === 'Yes',
      borrowerId: this.borrowerInfo?.borrowerId,
      previousExperience: borrowerFields.previousExperience.value === 'Yes'
    }
  }

  private toLicenseFileUpload(task: { taskId: number; originalFileName: string; fileExtension?: string }) {
    return {
      taskId: task.taskId,
      disabled: true,
      name: task.originalFileName,
      fileExtension: task.fileExtension,
      file: {
        path: task.originalFileName,
        name: task.originalFileName,
      },
    };
  }

  // Same query pattern as fetchUploadedTrackRecordFile: getTasks by
  // section_process / object_type / object_id (not getBorrowerTasksByName).
  *fetchBorrowerLicenseTask(
    borrowerId: number,
    sectionProcess: string,
    taskTemplateType: string,
  ) {
    const taskBody = {
      sectionProcess,
      objectType: 'BORROWER',
      taskTemplateType,
      objectId: borrowerId,
    };
    const response = yield this.borrowerDetailsVerificationService.getTasks(taskBody);
    const tasks = response?.data?.data || [];
    const allTasks = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
    if (allTasks.length === 0) return null;
    // Prefer the latest task that already has a file (for display).
    const tasksWithFile = allTasks.filter((task) => task.originalFileName);
    const candidateTasks = tasksWithFile.length > 0 ? tasksWithFile : allTasks;
    return candidateTasks.reduce(
      (latestTask, task) => (task.taskId > latestTask.taskId ? task : latestTask),
      candidateTasks[0],
    );
  }

  // Read existing GC / Broker license tasks from the loan task list (taskV2).
  loadLicensesFromLoanTodos(loanTodosStore: any): boolean {
    try {
      const byProcess = loanTodosStore?.todosByLoanProcess || {};
      const all = ([] as any[]).concat(...Object.values(byProcess));
      if (!all.length) return false;

      const borrowerId = this.borrowerInfo?.borrowerId || this.borrowerId;
      // A loan can have several borrowers → several "GC License" / "Broker
      const belongsToBorrower = (todo: any) => {
        if (!borrowerId) return true;
        const todoBorrowerId = (todo?.type2 === 'BORROWER_ID' ? todo?.typeId2 : null) ?? todo?.document?.borrowerId;
        return todoBorrowerId == null || Number(todoBorrowerId) === Number(borrowerId);
      };

      const findDocument = (matches: (doc: any) => boolean) =>
        all.find((todo) => belongsToBorrower(todo) && matches(todo?.document))?.document;
      const normalize = (value: any) => String(value || '').toLowerCase();
      const hasFile = (doc: any) => !!doc && (doc.canDownloadFile || !!doc.originalFileName);
      const toRow = (doc: any) =>
        hasFile(doc)
          ? [this.toLicenseFileUpload({
              taskId: doc.taskId,
              originalFileName: doc.originalFileName || doc.title || 'License document',
              fileExtension: doc.docType || doc.fileExtension,
            })]
          : [];

      const isGc = (doc: any) => {
        if (!doc) return false;
        const taskCode = normalize(doc.taskName || doc.taskCode);
        const title = normalize(doc.title);
        return taskCode.includes('gc_license') || taskCode.includes('general_contractor')
          || title === 'gc license' || title.includes('general contractor');
      };
      const isBroker = (doc: any) => {
        if (!doc) return false;
        const taskCode = normalize(doc.taskName || doc.taskCode);
        const title = normalize(doc.title);
        return taskCode.includes('broker_license') || taskCode.includes('re_license')
          || title === 're license' || title.includes('broker license')
          || title.includes('real estate broker') || title.includes('real estate agent');
      };

      const gcDoc = findDocument(isGc);
      const brokerDoc = findDocument(isBroker);

      this.gcLicenseTaskId = gcDoc?.taskId ?? null;
      this.brokerLicenseTaskId = brokerDoc?.taskId ?? null;

      const gcRow = toRow(gcDoc);
      const brokerRow = toRow(brokerDoc);

      if (!gcDoc && !brokerDoc) return false;

      this.setGeneralContractorUploads(gcRow);
      this.setBrokerUploads(brokerRow);
      return true;
    } catch (e) {
      console.error('Error reading licenses from loan todos:', e);
      return false;
    }
  }

  *getLoanBorrowerDocuments(_loanId?) {
    try {
      const id = this.borrowerInfo?.borrowerId || this.borrowerId;
      if (!id) {
        this.setBrokerUploads([]);
        this.setGeneralContractorUploads([]);
        this.gcLicenseTaskId = null;
        this.brokerLicenseTaskId = null;
        return;
      }

      const brokerTask = yield this.fetchBorrowerLicenseTask(
        id,
        'BORROWER_BROKER_LICENSE',
        'BORROWER_BROKER_LICENSE',
      );
      this.brokerLicenseTaskId = brokerTask?.taskId ?? null;
      this.setBrokerUploads(brokerTask?.originalFileName ? [this.toLicenseFileUpload(brokerTask)] : []);

      const gcTask = yield this.fetchBorrowerLicenseTask(
        id,
        'BORROWER_GC_LICENSE',
        'BORROWER_GC_LICENSE',
      );
      this.gcLicenseTaskId = gcTask?.taskId ?? null;
      this.setGeneralContractorUploads(gcTask?.originalFileName ? [this.toLicenseFileUpload(gcTask)] : []);
    } catch (e) {
      console.error('Error fetching borrower license documents:', e);
      this.setBrokerUploads([]);
      this.setGeneralContractorUploads([]);
      this.gcLicenseTaskId = null;
      this.brokerLicenseTaskId = null;
    }
  }

  // Resolve GC / Broker license task ids straight from taskV2 by loan + borrower
  *resolveLicensesByLoanAndBorrower(loanId: number, borrowerId: number) {
    try {
      if (!loanId || !borrowerId) return;
      const response = yield this.borrowerDetailsVerificationService.findByType1AndType2(
        'LOAN_ID', loanId, 'BORROWER_ID', borrowerId,
      );
      const tasks = response?.data?.data || [];
      const allTasks = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
      const normalize = (value: any) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const isGc = (code: string) => code.includes('gclicense') || code.includes('generalcontractor');
      const isBroker = (code: string) => code.includes('brokerlicense') || code.includes('relicense');
      const latestMatch = (matches: (code: string) => boolean) => {
        const found = allTasks.filter((task) => matches(normalize(task.taskCode || task.title)));
        return found.length
          ? found.reduce((latest, task) => (task.taskId > latest.taskId ? task : latest), found[0])
          : null;
      };
      this.gcLicenseTaskId = latestMatch(isGc)?.taskId ?? null;
      this.brokerLicenseTaskId = latestMatch(isBroker)?.taskId ?? null;
    } catch (e) {
      console.error('Error resolving licenses by loan + borrower:', e);
    }
  }

  // Borrower-only flow (no loanId): resolve GC / Broker license task ids from the
  // borrower-level master tasks (type1=BORROWER_ID) via taskV2 findByType1.
  *resolveLicensesByBorrower(borrowerId: number) {
    try {
      if (!borrowerId) return;
      const response = yield this.borrowerDetailsVerificationService.findByType1(
        'BORROWER_ID', borrowerId,
      );
      const tasks = response?.data?.data || [];
      const allTasks = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
      const normalize = (value: any) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const isGc = (code: string) => code.includes('gclicense') || code.includes('generalcontractor');
      const isBroker = (code: string) => code.includes('brokerlicense') || code.includes('relicense');
      const isTrackRecord = (code: string) => code.includes('trackrecord');
      const latestMatch = (matches: (code: string) => boolean) => {
        const found = allTasks.filter((task) => matches(normalize(task.taskCode || task.title)));
        return found.length
          ? found.reduce((latest, task) => (task.taskId > latest.taskId ? task : latest), found[0])
          : null;
      };
      this.gcLicenseTaskId = latestMatch(isGc)?.taskId ?? null;
      this.brokerLicenseTaskId = latestMatch(isBroker)?.taskId ?? null;

      // Track Record master file (borrower-only): resolve via taskV2 too, so we don't
      // hit the borrower-public getTasks endpoint (which rejects objectType=BORROWER).
      const trackRecordTaskId = latestMatch(isTrackRecord)?.taskId ?? null;
      if (trackRecordTaskId) {
        yield this.setTrackRecordFileByTaskId(trackRecordTaskId);
      }
    } catch (e) {
      console.error('Error resolving licenses by borrower:', e);
    }
  }

  // Set the Track Record popup file from a taskV2 master task id (borrower-only flow).
  *setTrackRecordFileByTaskId(taskId: number) {
    try {
      const response = yield this.borrowerDetailsVerificationService.findByTaskId(taskId);
      const task = response?.data?.data;
      const taskDocuments = Array.isArray(task?.taskDocuments) ? task.taskDocuments : [];
      const latestDocument = taskDocuments.length
        ? taskDocuments.reduce(
            (latest, current) => ((current?.taskDocumentId ?? 0) > (latest?.taskDocumentId ?? 0) ? current : latest),
            taskDocuments[0],
          )
        : null;
      this.uploadedTrackRecordFile = latestDocument?.originalFileName
        ? {
            taskId,
            originalFileName: latestDocument.originalFileName,
            fileExtension: (latestDocument.originalFileName || '').split('.').pop(),
          }
        : null;
    } catch (e) {
      console.error('Error resolving track record file by task id:', e);
      this.uploadedTrackRecordFile = null;
    }
  }

  // For each resolved license task id, fetch the task via taskV2 findByTaskId
  *fetchLicensesByTaskId() {
    try {
      if (this.gcLicenseTaskId) {
        const gcLicenseRow = yield this.fetchLicenseFileByTaskId(this.gcLicenseTaskId);
        if (gcLicenseRow) {
          this.setGeneralContractorUploads([gcLicenseRow]);
          this.trackRecordOtherFormStore.onFieldChange('generalContractor', 'Yes');
        }
      }
      if (this.brokerLicenseTaskId) {
        const brokerLicenseRow = yield this.fetchLicenseFileByTaskId(this.brokerLicenseTaskId);
        if (brokerLicenseRow) {
          this.setBrokerUploads([brokerLicenseRow]);
          this.trackRecordOtherFormStore.onFieldChange('broker', 'Yes');
        }
      }
    } catch (e) {
      console.error('Error fetching license tasks by id:', e);
    }
  }

  // GET /api/v1/taskV2/findByTaskId?taskId= → latest uploaded document
  *fetchLicenseFileByTaskId(taskId: number) {
    const response = yield this.borrowerDetailsVerificationService.findByTaskId(taskId);
    const task = response?.data?.data;
    const taskDocuments = Array.isArray(task?.taskDocuments) ? task.taskDocuments : [];
    const latestDocument = taskDocuments.length
      ? taskDocuments.reduce(
          (latest, current) => ((current?.taskDocumentId ?? 0) > (latest?.taskDocumentId ?? 0) ? current : latest),
          taskDocuments[0],
        )
      : null;
    if (!latestDocument?.originalFileName) return null;
    return this.toLicenseFileUpload({
      taskId,
      originalFileName: latestDocument.originalFileName,
      fileExtension: (latestDocument.originalFileName || '').split('.').pop(),
    });
  }

  // v2-only: fetch the borrower-level uploaded Track Record file for the popup.
  // Queries getTasks by object_type/id/section_process (not name); own observable.
  *fetchUploadedTrackRecordFile(borrowerId?: number) {
    try {
      const id = borrowerId || this.borrowerInfo?.borrowerId || this.borrowerId;
      if (!id) {
        this.uploadedTrackRecordFile = null;
        return;
      }

      // Read the master task the excel is written to. getTasks below stays as the fallback for
      // files still hanging off a legacy TRACK_RECORD_REVIEW task, which is what it filters by.
      const master = yield this.findTrackRecordMasterTask(id);
      if (master?.taskId) {
        yield this.setTrackRecordFileByTaskId(master.taskId);
        if (this.uploadedTrackRecordFile) return;
      }

      const taskBody = {
        sectionProcess: 'TRACK_RECORD_REVIEW',
        objectType: 'BORROWER',
        taskTemplateType: 'PROPERTY_TRACK_RECORD',
        objectId: id,
      };
      const response = yield this.borrowerDetailsVerificationService.getTasks(taskBody);
      const tasks = response?.data?.data || [];
      const fileTasks = Array.isArray(tasks)
        ? tasks.filter((t) => t && t.originalFileName)
        : [];
      const fileTask = fileTasks.length > 0
        ? fileTasks.reduce((latest, t) => (t.taskId > latest.taskId ? t : latest), fileTasks[0])
        : null;
      this.uploadedTrackRecordFile = fileTask
        ? {
            taskId: fileTask.taskId,
            originalFileName: fileTask.originalFileName,
            fileExtension: fileTask.fileExtension,
          }
        : null;
    } catch (e) {
      console.error('Error fetching uploaded track record file:', e);
      this.uploadedTrackRecordFile = null;
    }
  }

  *setGeneralContractorUploads(values) {
    this.generalContractorUploads = values;
  }

  *setBrokerUploads(values) {
    this.brokerUploads = values;
  }

  *setCurrentToken(token) {
    this.currentToken = token;
  }

  *setBorrowerInfo(borrower) {
    this.borrowerInfo = borrower;
  }

  resetActiveStep() {
    this.activeStep = 0;
    this.currentExpandedRowIndex = null;
  }

  *setDigitalTrackRecord(value) {
    this.digitalTrackRecord = value;
  }

  *setTemplateUploads(values) {
    this.templateUploads = values;
  }

  *validateTrackRecordTemplate() {
    if (!this.digitalTrackRecord) {
      if (this.templateUploads.length === 0) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Please upload the track record template to continue',
        });
      }
      else {
        this.continueFromTrackRecordStep();
      }
    }
    else {
      this.continueFromTrackRecordStep();
    }
  }

  *continueFromTrackRecordStep() {
    if (this.areValidParsedTrackRecordProperties()) {
      this.goNextStep();
    }
    else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please check the properties in red and resolve the issues in order to continue.',
      });
    }
  }

  *parseTrackRecordTemplate(callback?: () => void, options?: { forceTemplateParse?: boolean }) {
    try {
      this.loading = true;
      const shouldParse = options?.forceTemplateParse || !this.digitalTrackRecord;
      if (shouldParse) {
        if (this.templateUploads.length > 0) {
          const fileUpload = this.templateUploads[0];
          const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
          const newFile = new File([blob], fileUpload.name, { type: fileUpload.file.type });
          const formData = new FormData();
          formData.append('file', newFile);
          if (!this.borrowerInfo) {
            this.borrowerInfo = {};
            this.borrowerInfo.borrowerId = this.borrowerId
          }
          else if (!this.borrowerInfo.borrowerId) {
            this.borrowerInfo.borrowerId = this.borrowerId
          }

          const borrowerId = this.borrowerInfo?.borrowerId || this.borrowerId;
          if (!borrowerId) {
            this.globalStore.notificationStore.showErrorNotification({
              message: 'Borrower information is still loading. Please try again in a moment.',
            });
            return;
          }

          const response = yield this.borrowerDetailsVerificationService.parseTrackRecordTemplate(formData, borrowerId);
          const errorMsg = response.data.data;
          console.log('errorMsg', errorMsg);
          this.trackRecordsErrors = JSON.parse(errorMsg);
          if (Object.keys(this.trackRecordsErrors).length !== 0) {
            this.globalStore.notificationStore.showErrorNotification({
              message: 'Please check the properties in red and resolve the issues in order to continue.',
            });
          }
          yield this.fetchTrackRecordProperties();
          yield this.fetchBorrowerInfoAndEntities();
          if (callback) {
            callback();
          }
          yield this.setDigitalTrackRecord(!this.digitalTrackRecord);
        }
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while parsing track record template',
      });
    } finally {
      this.loading = false;
    }
  }

  resetExtractionState() {
    this.extractionFailed = false;
    this.extractionComplete = false;
    this.extractionTraceSteps = [];
    this.extractionResults = [];
    this.alreadySavedRecords = [];
    this.stopPolling();
  }

  /**
   * Records persisted by the original save flow (savePropertyInformation)
   * right before the agent was invoked. Used by saveExtractionResults to
   * avoid saving the same properties a second time.
   */
  setAlreadySavedRecords(records: any[]) {
    this.alreadySavedRecords = records || [];
  }

  private normalizeAddressPart(part?: string) {
    return (part || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private isAlreadySaved(result: TrackRecordAgentResponse) {
    const resultAddress = this.normalizeAddressPart(result['Property Address']);
    const resultCity = this.normalizeAddressPart(result['City']);
    const resultState = this.normalizeAddressPart(result['State']);
    const resultZip = this.normalizeAddressPart(result['Zipcode']);
    if (!resultAddress) return false;

    return this.alreadySavedRecords.some(saved => {
      // purchaseAddress may already contain the full "addr, city, state, zip"
      // string — split it so each component is compared independently.
      const savedAddressRaw = String(saved?.purchaseAddress || saved?.address || saved?.propertyAddress || '');
      const [rawStreet, rawCity, rawState, rawZip] = savedAddressRaw.split(',');
      const savedAddress = this.normalizeAddressPart(rawStreet);
      const savedCity = this.normalizeAddressPart(saved?.city || saved?.purchaseCity || rawCity);
      const savedState = this.normalizeAddressPart(saved?.state || saved?.purchaseState || rawState);
      const savedZip = this.normalizeAddressPart(saved?.zipCode || saved?.zipcode || saved?.purchaseZipcode || rawZip);

      // The street segment must match exactly; the remaining components must
      // match whenever both sides provide them. This avoids the false positives
      // substring matching produced for same street numbers / abbreviated names.
      if (!savedAddress || savedAddress !== resultAddress) return false;
      const cityMatches = !savedCity || !resultCity || savedCity === resultCity;
      const stateMatches = !savedState || !resultState || savedState === resultState;
      const zipMatches = !savedZip || !resultZip || savedZip === resultZip;
      return cityMatches && stateMatches && zipMatches;
    });
  }

  /**
   * Cancels the *interactive* extraction (the one driving the extraction
   * dialog). Fire-and-forget agent invocations are deliberately NOT aborted
   * here: they are triggered right before the dialog/component closes
   * ("Save and Finish") and must outlive the unmount — aborting them would
   * cancel the agent call the save flow just started.
   */
  cancelExtraction() {
    this.stopPolling();
    this.loading = false;
  }

  /**
   * Uploads the track record excel through the existing task-file pipeline
   * (createTask → uploadTaskFile → getFilePath) so the backend persists it
   * to storage (S3) and returns the file path the agent can read from.
   * Returns null on any failure — callers fall back to the bare file name.
   */
  // Shared by the read and write paths so they can't drift apart. Returns the whole task —
  // the write path posts it back verbatim in the uploadTaskFile form data.
  private async findTrackRecordMasterTask(borrowerId: number) {
    try {
      if (!borrowerId) return null;
      const response = await this.borrowerDetailsVerificationService.findByType1(
        BORROWER_TYPE_ID, borrowerId,
      );
      const tasks = response?.data?.data || [];
      const matches = (Array.isArray(tasks) ? tasks : []).filter(
        (task) => task?.taskId
          && (task.taskCode === TRACK_RECORD_TASK_NAME || task.title === TRACK_RECORD_TASK_NAME),
      );
      return matches.length
        ? matches.reduce((latest, task) => (task.taskId > latest.taskId ? task : latest), matches[0])
        : null;
    } catch (e) {
      console.error('Error resolving the Track Record master task:', e);
      return null;
    }
  }

  private async uploadTrackRecordExcelToStorage(borrowerId: number): Promise<string | null> {
    try {
      if (this.templateUploads.length === 0) return null;
      const fileUpload = this.templateUploads[0];

      // Prefer the borrower-level master task (type1=BORROWER_ID) so the excel is stored once
      // per borrower and every loan reads the same file. createTask below stays as the fallback
      // for borrowers that have no master yet.
      let task: any = await this.findTrackRecordMasterTask(borrowerId);
      if (!task) {
        const taskBody = {
          sectionProcess: 'TRACK_RECORD_REVIEW',
          objectType: 'BORROWER',
          taskTemplateType: 'PROPERTY_TRACK_RECORD',
          objectId: borrowerId,
        };
        const taskResponse = await this.borrowerDetailsVerificationService.createTask(taskBody);
        task = taskResponse?.data?.data?.[0];
      }
      if (!task) return null;

      const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
      const newFile = new File([blob], fileUpload.name, { type: fileUpload.file.type });
      const formData = new FormData();
      formData.append('file', newFile);
      formData.append('task', JSON.stringify(task));
      const uploadResponse = await this.borrowerDetailsVerificationService.uploadTaskFile(formData);
      const uploaded = uploadResponse?.data?.data;
      const uploadedTask = Array.isArray(uploaded) ? uploaded[0] : uploaded;

      const fileDownloadId = uploadedTask?.fileDownloadId
        || task?.fileDownloadId
        || task?.taskId?.toString();
      // getFilePath has no real route (always 404) — return the id directly.
      return fileDownloadId || null;
    } catch (error) {
      console.error('Error uploading track record excel to storage:', error);
      return null;
    }
  }

  invokeAgent(options?: {
    loanId?: number;
    trackRecordExcel?: string;
    trackRecordEntity?: string;
    trackRecordPropertyAddress?: string;
    entityNames?: string[];
    customTransactions?: Array<{
      street_address?: string;
      street_address_line_2?: string;
      city?: string;
      state?: string;
      zip_code?: string;
    }>;
    bridge?: boolean;
    groundUp?: boolean;
    term?: boolean;
    recordSourceType?: string;
    manualProperty?: {
      property_address?: string;
      city?: string;
      state?: string;
      zipcode?: string;
      type_of_deal?: string;
      purchase_price?: string;
      purchase_date?: string;
      sale_price?: string;
      sale_date?: string;
      renovation_budget?: string;
    };
  }) {
    this.extractionFailed = false;
    this.extractionComplete = false;
    this.extractionTraceSteps = [];
    this.extractionResults = [];
    this.loading = true;

    if (!this.borrowerInfo?.borrowerId && this.borrowerId) {
      if (!this.borrowerInfo) this.borrowerInfo = {};
      this.borrowerInfo.borrowerId = this.borrowerId;
    }

    const borrowerId = this.borrowerInfo?.borrowerId
      || this.borrowerInfo?.borrower_id
      || this.borrowerInfo?.id
      || this.borrowerId;
    if (!borrowerId) {
      this.extractionFailed = true;
      this.loading = false;
      return;
    }

    const isUploadFlow = !!options?.trackRecordExcel || (!options?.trackRecordEntity && !options?.trackRecordPropertyAddress && !options?.entityNames && !options?.customTransactions);

    this.stopPolling();

    const startAndPoll = async () => {
      try {
        // Upload flow: parse the template (existing behavior) and, in parallel,
        // push the file to storage (S3) to get a real path for the agent.
        let trackRecordExcelPath: string | null = null;
        if (isUploadFlow && this.templateUploads.length > 0) {
          const fileUpload = this.templateUploads[0];
          const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
          const newFile = new File([blob], fileUpload.name, { type: fileUpload.file.type });
          const formData = new FormData();
          formData.append('file', newFile);
          const [, excelPath] = await Promise.all([
            this.borrowerDetailsVerificationService.parseTrackRecordTemplate(formData, borrowerId),
            this.uploadTrackRecordExcelToStorage(borrowerId),
          ]);
          trackRecordExcelPath = excelPath;
        }

        // Start the agent job (POST) — the agent runs server-side regardless of
        // this client connection. We then poll its status/stages/result.
        const startResp = await this.borrowerDetailsVerificationService.startTrackRecordAgent({
          borrower_id: borrowerId,
          loan_id: options?.loanId || undefined,
          gc_license: this.gcLicenseFileId || undefined,
          re_license: this.reLicenseFileId || undefined,
          track_record_excel: trackRecordExcelPath || options?.trackRecordExcel || undefined,
          track_record_entity: options?.trackRecordEntity || undefined,
          track_record_property_address: options?.trackRecordPropertyAddress || undefined,
          entity_names: options?.entityNames || undefined,
          custom_transactions: options?.customTransactions || undefined,
          bridge: options?.bridge,
          ground_up: options?.groundUp,
          term: options?.term,
          record_source_type: options?.recordSourceType || undefined,
          manual_property: options?.manualProperty || undefined,
        }) as any;
        const bedrockAgentInvocationId = startResp?.data?.bedrockAgentInvocationId;
        if (!bedrockAgentInvocationId) {
          runInAction(() => { this.extractionFailed = true; this.loading = false; });
          return;
        }
        this.pollJob(bedrockAgentInvocationId);
      } catch (error) {
        console.error('Error starting track record agent:', error);
        runInAction(() => {
          this.extractionFailed = true;
          this.loading = false;
        });
      }
    };

    startAndPoll();
  }

  // Hard cap on poll duration. Without it, a job stuck in any non-terminal status
  // (IN_PROGRESS or an unexpected value) would keep the dialog spinning forever.
  private static readonly POLL_TIMEOUT_MS = 5 * 60 * 1000;

  private pollJob(bedrockAgentInvocationId: number | string) {
    const startedAt = Date.now();
    const poll = async () => {
      if (Date.now() - startedAt > BorrowerTrackRecordVerificationStore.POLL_TIMEOUT_MS) {
        runInAction(() => {
          this.extractionFailed = true;
          this.loading = false;
        });
        this.stopPolling();
        return;
      }
      try {
        const resp = await this.borrowerDetailsVerificationService.getTrackRecordAgentJob(bedrockAgentInvocationId) as any;
        const data = resp?.data || {};
        runInAction(() => {
          if (Array.isArray(data.stages)) {
            this.extractionTraceSteps = data.stages;
          }
          const status = data.status;
          if (status === 'SUCCESS' || status === 'NO_RESULTS') {
            const result = data.result;
            this.extractionResults = (result && Array.isArray(result.results))
              ? result.results
              : (Array.isArray(result) ? result : []);
            this.extractionComplete = true;
            this.loading = false;
            this.stopPolling();
          } else if (status === 'PARSE_FAILED' || status === 'AGENT_ERROR') {
            this.extractionFailed = true;
            this.loading = false;
            this.stopPolling();
          }
        });
      } catch (e) {
        // Transient poll error — keep polling; the timeout above guarantees we
        // eventually give up if the server never reaches a terminal status.
      }
    };
    poll();
    this.pollHandle = setInterval(poll, 2500);
  }

  private stopPolling() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  /**
   * Fire-and-forget invocation of the Bedrock track-record agent.
   * The result is intentionally ignored: the FE relies on the original
   * save APIs (savePropertyInformation / parseTrackRecordTemplate) for data.
   */
  invokeAgentFireAndForget(options?: {
    loanId?: number;
    trackRecordExcel?: string;
    trackRecordEntity?: string;
    trackRecordPropertyAddress?: string;
    entityNames?: string[];
    customTransactions?: Array<{
      street_address?: string;
      street_address_line_2?: string;
      city?: string;
      state?: string;
      zip_code?: string;
    }>;
    bridge?: boolean;
    groundUp?: boolean;
    term?: boolean;
    recordSourceType?: string;
    manualProperty?: {
      property_address?: string;
      city?: string;
      state?: string;
      zipcode?: string;
      type_of_deal?: string;
      purchase_price?: string;
      purchase_date?: string;
      sale_price?: string;
      sale_date?: string;
      renovation_budget?: string;
    };
  }) {
    if (!this.borrowerInfo?.borrowerId && this.borrowerId) {
      if (!this.borrowerInfo) this.borrowerInfo = {};
      this.borrowerInfo.borrowerId = this.borrowerId;
    }

    const borrowerId = this.borrowerInfo?.borrowerId
      || this.borrowerInfo?.borrower_id
      || this.borrowerInfo?.id
      || this.borrowerId;
    if (!borrowerId) {
      return undefined;
    }

    const run = async (): Promise<{ requestId: string | null; status: string | null } | null> => {
      try {
        // Upload flow: push the excel to storage (S3) first so the agent
        // receives a real file path instead of just the file name.
        // Parsing has already happened separately (on file drop).
        let trackRecordExcelPath: string | null = null;
        if (options?.trackRecordExcel && this.templateUploads.length > 0) {
          trackRecordExcelPath = await this.uploadTrackRecordExcelToStorage(borrowerId);
        }

        // Start the job (POST). The ack only carries request_id and status.
        const response: any = await this.borrowerDetailsVerificationService.startTrackRecordAgent({
          borrower_id: borrowerId,
          loan_id: options?.loanId || undefined,
          gc_license: this.gcLicenseFileId || undefined,
          re_license: this.reLicenseFileId || undefined,
          track_record_excel: trackRecordExcelPath || options?.trackRecordExcel || undefined,
          track_record_entity: options?.trackRecordEntity || undefined,
          track_record_property_address: options?.trackRecordPropertyAddress || undefined,
          entity_names: options?.entityNames || undefined,
          custom_transactions: options?.customTransactions || undefined,
          bridge: options?.bridge,
          ground_up: options?.groundUp,
          term: options?.term,
          record_source_type: options?.recordSourceType || undefined,
          manual_property: options?.manualProperty || undefined,
        });
        return {
          requestId: response?.data?.request_id ?? null,
          status: response?.data?.status ?? null,
        };
      } catch (error) {
        // fire-and-forget: intentionally ignore agent errors
        return null;
      }
    };

    // Do not await — the agent runs and persists server-side. Callers that care
    // about the request_id can chain onto the returned promise.
    return run();
  }

  // Queues an Entity/Property lookup fired via invokeAgentFireAndForget. Polling starts once /invoke
  // acks. The ack only carries a request_id, so the numeric invocation id is resolved once from the
  // overview's in_progress list; after that only the invocation status endpoint is polled.
  queueTrackRecordSearch(label: string, flowType: 'ENTITY_LOOKUP' | 'PROPERTY_LOOKUP' | 'UPLOAD', agentOptions: any) {
    const localId = `${Date.now()}-${this.pendingSearchCounter++}`;
    this.pendingAgentSearches = [
      ...this.pendingAgentSearches,
      {
        id: localId,
        label,
        flow: flowType,
        queuedAt: Date.now(),
        confirmedAt: null,
        requestId: null,
        bedrockAgentInvocationId: null,
        status: 'pending',
        finishedAt: null,
        resultCount: null,
      },
    ];

    Promise.resolve(this.invokeAgentFireAndForget(agentOptions)).then((ack) => {
      runInAction(() => {
        if (!ack?.requestId || ack.status === 'ERROR') {
          this.dismissAgentSearch(localId);
          this.globalStore.notificationStore.showErrorNotification({
            message: `We couldn't start the search for "${label}". Please try again.`,
          });
          return;
        }
        this.globalStore.notificationStore.showSuccessNotification({
          message: `${flowType === 'ENTITY_LOOKUP' ? 'Entity' : flowType === 'UPLOAD' ? 'Track record file' : 'Property'} added to verification queue successfully`,
        });
        this.pendingAgentSearches = this.pendingAgentSearches.map((pending) =>
          pending.id === localId
            ? {
                ...pending,
                requestId: ack.requestId,
              }
            : pending
        );
        this.ensurePendingSearchPolling();
      });
    });
  }

  // Dismisses a completed (or stuck) entry from both the top banner and the
  // Entity/Property lookup panels — they share this same array.
  dismissAgentSearch(id: string) {
    this.pendingAgentSearches = this.pendingAgentSearches.filter((pending) => pending.id !== id);
    // Dismissing the last running search ends the polling; the agent itself keeps running server-side.
    if (!this.pendingAgentSearches.some((pending) => pending.status === 'pending')) {
      this.stopPendingSearchPolling();
    }
  }

  private ensurePendingSearchPolling() {
    if (this.pendingSearchPollHandle) return;
    const poll = () => this.reconcilePendingAgentSearches();
    poll();
    this.pendingSearchPollHandle = setInterval(poll, 5000);
  }

  private stopPendingSearchPolling() {
    if (this.pendingSearchPollHandle) {
      clearInterval(this.pendingSearchPollHandle);
      this.pendingSearchPollHandle = null;
    }
  }

  // Hard cap: drop a queued search whose request_id never shows up in the overview
  // response, so the top banner can't get stuck forever.
  private static readonly PENDING_SEARCH_TIMEOUT_MS = 5 * 60 * 1000;

  // Cap for a matched search whose status endpoint never resolves (404/500) so polling can't run forever.
  private static readonly MATCHED_SEARCH_TIMEOUT_MS = 15 * 60 * 1000;

  private static readonly STATUS_SUCCESS = 'SUCCESS';

  private static readonly FAILED_STATUSES = new Set(['PARSE_FAILED', 'AGENT_ERROR']);

  *reconcilePendingAgentSearches() {
    if (!this.pendingAgentSearches.some((pending) => pending.status === 'pending')) {
      this.stopPendingSearchPolling();
      return;
    }
    if (this.isReconcilingPendingSearches) return;
    this.isReconcilingPendingSearches = true;
    try {
      const id = this.borrowerInfo?.borrowerId ?? this.borrowerId;
      if (!id) return;

      // The overview is only needed to find the invocation id of a search that doesn't have one yet.
      const unmatched = this.pendingAgentSearches.filter(
        (pending) => pending.status === 'pending' && !pending.bedrockAgentInvocationId
      );
      if (unmatched.length > 0) {
        try {
          yield this.fetchTrackRecordOverview(id);
        } catch (error) {
          console.error(error);
        }
        const rawItems: any[] = this.trackRecordOverviewItems ?? [];
        this.pendingAgentSearches = this.pendingAgentSearches.map((pending) => {
          if (pending.status !== 'pending' || pending.bedrockAgentInvocationId || !pending.requestId) return pending;
          const matchedItem = rawItems.find((item) => item.request_id === pending.requestId);
          if (!matchedItem?.bedrock_agent_invocation_id) return pending;
          return {
            ...pending,
            bedrockAgentInvocationId: matchedItem.bedrock_agent_invocation_id,
            confirmedAt: pending.confirmedAt ?? (Date.parse(matchedItem.creation_date) || Date.now()),
          };
        });
      }

      const toCheck = this.pendingAgentSearches.filter(
        (pending) => pending.status === 'pending' && pending.bedrockAgentInvocationId
      );
      const succeededIds = new Set<string>();
      const silentlyRemoveIds = new Set<string>();
      const failedIds = new Set<string>();
      if (toCheck.length > 0) {
        const statuses = yield Promise.all(
          toCheck.map((pending) =>
            this.borrowerDetailsVerificationService
              .getBedrockAgentInvocation(pending.bedrockAgentInvocationId)
              .then((response: any) => ({ id: pending.id, status: (response?.data?.status ?? '').toString().toUpperCase() }))
              .catch(() => ({ id: pending.id, status: null }))
          )
        );
        statuses.forEach(({ id: pendingId, status }) => {
          if (status === BorrowerTrackRecordVerificationStore.STATUS_SUCCESS) {
            succeededIds.add(pendingId);
          } else if (status && BorrowerTrackRecordVerificationStore.FAILED_STATUSES.has(status)) {
            failedIds.add(pendingId);
          } else if (status === 'NO_RESULTS') {
            silentlyRemoveIds.add(pendingId);
          }
        });
      }

      this.pendingAgentSearches
        .filter((pending) => failedIds.has(pending.id))
        .forEach((pending) =>
          this.globalStore.notificationStore.showErrorNotification({
            message: `The search for "${pending.label}" failed. Please try again.`,
          })
        );

      const now = Date.now();
      this.pendingAgentSearches = this.pendingAgentSearches.filter((pending) => {
        if (silentlyRemoveIds.has(pending.id) || failedIds.has(pending.id)) return false;
        if (
          pending.status === 'pending' && !pending.bedrockAgentInvocationId
          && now - pending.queuedAt > BorrowerTrackRecordVerificationStore.PENDING_SEARCH_TIMEOUT_MS
        ) {
          return false; // never showed up server-side — drop it
        }
        if (
          pending.status === 'pending' && pending.bedrockAgentInvocationId
          && now - (pending.confirmedAt ?? pending.queuedAt) > BorrowerTrackRecordVerificationStore.MATCHED_SEARCH_TIMEOUT_MS
        ) {
          return false; // matched but its status never resolved — stop polling it
        }
        return true;
      });

      // Any finished run (incl. no results / failed) refreshes the overview so the agent runs table updates.
      if (succeededIds.size > 0 || silentlyRemoveIds.size > 0 || failedIds.size > 0) {
        yield this.loadFromOverview(id);
      }

      if (succeededIds.size > 0) {
        const finishedAt = Date.now();
        this.pendingAgentSearches = this.pendingAgentSearches.map((pending) => {
          if (!succeededIds.has(pending.id)) return pending;
          const resultCount = (this.properties ?? []).filter(
            (property: any) => property.bedrockAgentInvocationId === pending.bedrockAgentInvocationId
          ).length;
          return { ...pending, status: 'done', finishedAt, resultCount };
        });
      }

      if (!this.pendingAgentSearches.some((pending) => pending.status === 'pending')) {
        this.stopPendingSearchPolling();
      }
    } finally {
      this.isReconcilingPendingSearches = false;
    }
  }

  // Single upload path; enqueueForDocReview is opt-in so only the new V2 flow triggers doc-review automation.
  *uploadLicenseFile(sectionProcess, fresh, existingTaskId, documentStore, enqueueForDocReview = false) {
    const borrowerId = this.borrowerInfo?.borrowerId || this.borrowerId;
    if (!borrowerId) return null;
    const taskBody = { sectionProcess, objectType: 'BORROWER', taskTemplateType: sectionProcess, objectId: borrowerId };

    // Replace: upload into the already-existing task via taskV2 (like the standalone task).
    if (existingTaskId && documentStore?.uploadDocument) {
      yield documentStore.uploadDocument(fresh.file, { taskId: existingTaskId, taskName: sectionProcess }, UNDERWRITING);
      this.markLicenseClaimed(sectionProcess);
      if (enqueueForDocReview) this.enqueueLicenseForDocReview(existingTaskId, sectionProcess, borrowerId);
      return { taskId: existingTaskId };
    }

    // First upload: create the task and upload in one consistent borrower-public call.
    const task = yield this.createTaskWithFile(taskBody, fresh);
    if (!task?.taskId) return null;
    this.markLicenseClaimed(sectionProcess);
    if (enqueueForDocReview) this.enqueueLicenseForDocReview(task.taskId, sectionProcess, borrowerId);
    return { taskId: task.taskId };
  }

  /**
   * The Licenses tab lets a document be uploaded while the radio still says "No" (its upload
   * action is gated on the verified status, not on the claimed flag). Save then posts that
   * stale "No" and leaves general_contractor / broker false despite the license being stored,
   * so the answer is aligned here — the same rule setBorrowerFields() applies.
   */
  markLicenseClaimed(sectionProcess: string) {
    const field = sectionProcess === 'BORROWER_GC_LICENSE'
      ? 'generalContractor'
      : sectionProcess === 'BORROWER_BROKER_LICENSE'
        ? 'broker'
        : null;
    if (field) {
      this.trackRecordOtherFormStore.onFieldChange(field, 'Yes');
    }
  }

  /**
   * Queue a freshly uploaded GC / Broker license for AI doc-review automation.
   */
  private async enqueueLicenseForDocReview(taskId: number, sectionProcess: string, borrowerId: number) {
    try {
      const taskCode = LICENSE_DOC_REVIEW_TASK_CODE[sectionProcess];
      if (!taskCode || !taskId || !borrowerId) return;

      const response = await this.borrowerDetailsVerificationService.findByTaskId(taskId);
      const taskDocuments = response?.data?.data?.taskDocuments;
      const latestDocument = Array.isArray(taskDocuments) && taskDocuments.length
        ? taskDocuments.reduce(
            (latest, current) => ((current?.taskDocumentId ?? 0) > (latest?.taskDocumentId ?? 0) ? current : latest),
            taskDocuments[0],
          )
        : null;

      const filePath = latestDocument?.path;
      if (!filePath) return;

      await this.borrowerDetailsVerificationService.enqueueDocReview({
        borrowerId,
        taskCode,
        filePath,
        taskId,
      });
    } catch (error) {
      // intentionally swallowed: automation is best-effort
      console.error('Error enqueuing license for doc review:', error);
    }
  }

  *setUploadingLicense(value) {
    this.isUploadingLicense = value;
  }

  *saveExtractionResults() {
    try {
      this.loading = true;
      // Skip results already persisted by the original save flow
      // (savePropertyInformation in the lookup popups) to avoid duplicates.
      const newResults = this.extractionResults.filter(result => !this.isAlreadySaved(result));
      for (const result of newResults) {
        const propertyData = {
          purchaseAddress: [
            result['Property Address'],
            result['City'],
            result['State'],
            result['Zipcode'],
          ].filter(Boolean).join(', '),
          dealType: result['Type of Deal'] || '',
          purchasePrice: result['Purchase Price'] || '',
          purchaseDate: result['Purchase Date'] || '',
          rehabBudget: result['Renovation Budget'] || '',
          salePrice: result['Sale Price'] || '',
          saleDate: result['Sale Date'] || '',
          entityName: result['Entity Name'] || '',
          isVerified: result.validation?.isVerified || false,
          borrowerId: this.borrowerInfo?.borrowerId,
        };
        yield this.borrowerDetailsVerificationService.saveTrackRecordProperty(propertyData);
      }
      yield this.fetchTrackRecordProperties();
      yield this.fetchBorrowerInfoAndEntities();
    } catch (error) {
      console.error('Error saving extraction results:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while saving extraction results',
      });
    } finally {
      this.loading = false;
    }
  }

  *parseUploadedTrackRecord() {
    try {
      this.extractionFailed = false;
      this.extractionComplete = false;
      this.loading = true;

      if (this.templateUploads.length === 0) {
        this.extractionFailed = true;
        return;
      }

      const fileUpload = this.templateUploads[0];
      const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
      const newFile = new File([blob], fileUpload.name, { type: fileUpload.file.type });
      const formData = new FormData();
      formData.append('file', newFile);

      if (!this.borrowerInfo) {
        this.borrowerInfo = {};
        this.borrowerInfo.borrowerId = this.borrowerId;
      } else if (!this.borrowerInfo.borrowerId) {
        this.borrowerInfo.borrowerId = this.borrowerId;
      }

      const response = yield this.borrowerDetailsVerificationService.parseTrackRecordTemplate(formData, this.borrowerInfo?.borrowerId);
      const errorMsg = response.data.data;
      this.trackRecordsErrors = JSON.parse(errorMsg);
      yield this.fetchTrackRecordProperties();
      yield this.fetchBorrowerInfoAndEntities();
      this.extractionComplete = true;
    } catch (error) {
      console.error(error);
      this.extractionFailed = true;
    } finally {
      this.loading = false;
    }
  }

  *isTrackRecordTemplateParsingAllowed() {
    try {
      const response = yield this.borrowerDetailsVerificationService.isTrackRecordTemplateParsingAllowed();
      this.trackRecordTemplateParsingAllowed = response.data.data;
    } catch (e) {
      console.error(e);
    }
  }

  doesPropertyHasParsingError = (address: string) => {
    return this.trackRecordsErrors[address]?.length > 0;
  }

  areValidParsedTrackRecordProperties = () => {
    let validParsedProperties = true;
    this.properties.forEach(property => {
      if (this.doesPropertyHasParsingError(property.purchaseAddress)) {
        this.trackRecordPropertyFormStore.loadForm(property);
        this.trackRecordPropertyFormStore.runFormValidation();
        if (!this.trackRecordPropertyFormStore.form.meta.isValid) {
          validParsedProperties = false;
        }
      }
    });
    return validParsedProperties;
  }

  setIsTpoFlow(isTpo: boolean) {
    this.isTpoFlow = isTpo;
  }

  setShouldPropagateDealType(shouldPropagateDealType: boolean) {
    this.shouldPropagateDealType = shouldPropagateDealType;
  }

  checkBorrowerId(borrowerId?: number | string) {
    if (borrowerId) {
      this.borrowerInfo = { borrowerId };
      this.borrowerId = borrowerId;
    }
  }
}
